import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { postApi, putApi } from '../../../Services/apiService';

const Settings = () => {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('profile');
    const [isEditMode, setIsEditMode] = useState(false);
    const [profileData, setProfileData] = useState({
        name: 'Adam Smith',
        email: 'admin@resumate.com',
        phone: '9383828385',
        profilePicture: null
    });
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [loader, setLoader] = useState(false);
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const togglePasswordVisibility = (field) => {
        setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
    };

    useEffect(() => {
        // Load user data from localStorage or API
        const userDetail = JSON.parse(localStorage.getItem('userDetail') || '{}');
        if (userDetail) {
            setProfileData({
                name: userDetail.name || userDetail.first_name || 'Adam Smith',
                email: userDetail.email || 'admin@resumate.com',
                phone: userDetail.phone || userDetail.phone_number || '9383828385',
                profilePicture: userDetail.profile_picture || null
            });
        }
    }, []);

    const handleProfileInputChange = (e) => {
        const { name, value, files } = e.target;
        
        if (name === 'profilePicture' && files && files[0]) {
            const file = files[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileData(prev => ({
                    ...prev,
                    profilePicture: reader.result
                }));
            };
            reader.readAsDataURL(file);
        } else {
            setProfileData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handlePasswordInputChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateProfileForm = () => {
        const errors = {};
        
        if (!profileData.name.trim()) {
            errors.name = 'Name is required';
        }
        
        if (!profileData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        
        if (!profileData.phone.trim()) {
            errors.phone = 'Phone number is required';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validatePasswordForm = () => {
        const errors = {};
        
        if (!passwordData.currentPassword.trim()) {
            errors.currentPassword = 'Current password is required';
        }
        
        if (!passwordData.newPassword.trim()) {
            errors.newPassword = 'New password is required';
        } else if (passwordData.newPassword.length < 6) {
            errors.newPassword = 'Password must be at least 6 characters';
        }
        
        if (!passwordData.confirmPassword.trim()) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (passwordData.newPassword !== passwordData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();

        if (!validateProfileForm()) {
            return;
        }

        const userDetail = JSON.parse(localStorage.getItem('userDetail') || '{}');
        const userId = userDetail.id ?? userDetail.user_id ?? userDetail.uuid;
        if (!userId) {
            toast.error('Unable to update profile: user ID not found. Please sign in again.');
            return;
        }

        setLoader(true);
        try {
            const payload = {
                name: profileData.name.trim(),
                phoneNumber: profileData.phone.trim(),
            };
            const response = await putApi(`/api/v1/admin/users/${userId}`, payload);

            if (response?.statusCode === 200 || response?.statusCode === 201) {
                toast.success(response?.message || 'Profile updated successfully');
                const updatedUserDetail = {
                    ...userDetail,
                    name: profileData.name.trim(),
                    first_name: profileData.name.trim(),
                    phone: profileData.phone.trim(),
                    phone_number: profileData.phone.trim(),
                };
                localStorage.setItem('userDetail', JSON.stringify(updatedUserDetail));
                setIsEditMode(false);
            } else if (response?.message || response?.error) {
                toast.error(response?.message || response?.error || 'Failed to update profile.');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error(error?.response?.data?.message || error?.response?.data?.error || 'Failed to update profile. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        
        if (!validatePasswordForm()) {
            return;
        }
        
        setLoader(true);
        try {
            const payload = {
                old_password: passwordData.currentPassword,
                new_password: passwordData.newPassword,
            };

            const response = await postApi('/api/v1/change-password', payload, {}, false);

            if (response?.statusCode === 200 || response?.statusCode === 201) {
                toast.success(response?.message || 'Password changed successfully. Please sign in again.');
                setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                localStorage.clear();
                sessionStorage.clear();
                navigate('/login', { replace: true });
            } else if (response) {
                toast.error(response?.message || response?.error || 'Failed to change password. Please try again.');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            toast.error('Failed to change password. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    const handleEditClick = () => {
        setIsEditMode(true);
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        // Reset to original values if needed
        const userDetail = JSON.parse(localStorage.getItem('userDetail') || '{}');
        if (userDetail) {
            setProfileData({
                name: userDetail.name || userDetail.first_name || 'Adam Smith',
                email: userDetail.email || 'admin@resumate.com',
                phone: userDetail.phone || userDetail.phone_number || '9383828385',
                profilePicture: userDetail.profile_picture || null
            });
        }
    };

    const handleCameraClick = () => {
        document.getElementById('profilePictureInput').click();
    };

    return (
        <div className='RightSideDiv'>
            <div className="settings-container">
                <div className="settings-sidebar">
                    <button
                        className={`settings-option ${activeSection === 'profile' ? 'active' : ''}`}
                        onClick={() => setActiveSection('profile')}
                    >
                        <span>Profile</span>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                    <button
                        className={`settings-option ${activeSection === 'password' ? 'active' : ''}`}
                        onClick={() => setActiveSection('password')}
                    >
                        <span>Change Password</span>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <div className="settings-content">
                    {activeSection === 'profile' && (
                        <div className="profile-section">
                            <div className="profile-card">
                                <div className="profile-header">
                                    <div className="profile-picture-container">
                                        <img
                                            src={profileData.profilePicture || window.getPublicUrl('/assets/images/dummy-profile.jpg')}
                                            alt="Profile"
                                            className="profile-picture"
                                        />
                                        {isEditMode && (
                                            <div className="camera-icon-overlay" onClick={handleCameraClick}>
                                                <img src={window.getPublicUrl('/assets/images/Camera.svg')} alt="Upload" />
                                            </div>
                                        )}
                                    </div>
                                    {!isEditMode ? (
                                        <button className="edit-icon-btn" onClick={handleEditClick}>
                                            <img src={window.getPublicUrl('/assets/images/profileEditIcon.svg')} alt="Edit" />
                                        </button>
                                    ) : (
                                        <div className="profile-header-actions">
                                            <button
                                                type="button"
                                                className="cancel-btn-header"
                                                onClick={handleCancelEdit}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                form="profile-form"
                                                className="submit-btn-header"
                                                disabled={loader}
                                            >
                                                {loader ? 'Saving...' : 'Save Changes'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <form id="profile-form" onSubmit={handleProfileSubmit}>
                                    <div className="profile-form">
                                        <div className="form-row">
                                            <div className="form-group form-group-half">
                                                <label htmlFor="name">Name</label>
                                                <input
                                                    type="text"
                                                    id="name"
                                                    name="name"
                                                    value={profileData.name}
                                                    onChange={handleProfileInputChange}
                                                    disabled={!isEditMode}
                                                    className={formErrors.name ? 'error' : ''}
                                                />
                                                {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                                            </div>

                                            <div className="form-group form-group-half">
                                                <label htmlFor="email">Email</label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    value={profileData.email}
                                                    readOnly
                                                    disabled
                                                    className={`${formErrors.email ? 'error' : ''} input-readonly`}
                                                    aria-label="Email (read-only)"
                                                />
                                                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="phone">Phone number</label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={profileData.phone}
                                                onChange={handleProfileInputChange}
                                                disabled={!isEditMode}
                                                className={formErrors.phone ? 'error' : ''}
                                            />
                                            {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                                        </div>

                                        <input
                                            type="file"
                                            id="profilePictureInput"
                                            name="profilePicture"
                                            accept="image/*"
                                            onChange={handleProfileInputChange}
                                            style={{ display: 'none' }}
                                        />
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {activeSection === 'password' && (
                        <div className="password-section">
                            <div className="profile-card">
                                <form id="password-form" onSubmit={handlePasswordSubmit}>
                                    <div className="profile-form">
                                        <div className="form-group">
                                            <label htmlFor="currentPassword">Old password</label>
                                            <div className="password-input-wrap">
                                                <input
                                                    type={showPassword.current ? 'text' : 'password'}
                                                    id="currentPassword"
                                                    name="currentPassword"
                                                    value={passwordData.currentPassword}
                                                    onChange={handlePasswordInputChange}
                                                    className={formErrors.currentPassword ? 'error' : ''}
                                                    placeholder=" "
                                                />
                                                <button
                                                    type="button"
                                                    className="password-eye-toggle"
                                                    onClick={() => togglePasswordVisibility('current')}
                                                    aria-label={showPassword.current ? 'Hide password' : 'Show password'}
                                                >
                                                    <img src={showPassword.current ? window.getPublicUrl('/assets/images/Eye.svg') : window.getPublicUrl('/assets/images/Eye-closed.svg')} alt="" />
                                                </button>
                                            </div>
                                            {formErrors.currentPassword && <span className="error-message">{formErrors.currentPassword}</span>}
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="newPassword">New password</label>
                                            <div className="password-input-wrap">
                                                <input
                                                    type={showPassword.new ? 'text' : 'password'}
                                                    id="newPassword"
                                                    name="newPassword"
                                                    value={passwordData.newPassword}
                                                    onChange={handlePasswordInputChange}
                                                    className={formErrors.newPassword ? 'error' : ''}
                                                    placeholder=" "
                                                />
                                                <button
                                                    type="button"
                                                    className="password-eye-toggle"
                                                    onClick={() => togglePasswordVisibility('new')}
                                                    aria-label={showPassword.new ? 'Hide password' : 'Show password'}
                                                >
                                                    <img src={showPassword.new ? window.getPublicUrl('/assets/images/Eye.svg') : window.getPublicUrl('/assets/images/Eye-closed.svg')} alt="" />
                                                </button>
                                            </div>
                                            {formErrors.newPassword && <span className="error-message">{formErrors.newPassword}</span>}
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="confirmPassword">Confirm password</label>
                                            <div className="password-input-wrap">
                                                <input
                                                    type={showPassword.confirm ? 'text' : 'password'}
                                                    id="confirmPassword"
                                                    name="confirmPassword"
                                                    value={passwordData.confirmPassword}
                                                    onChange={handlePasswordInputChange}
                                                    className={formErrors.confirmPassword ? 'error' : ''}
                                                    placeholder=" "
                                                />
                                                <button
                                                    type="button"
                                                    className="password-eye-toggle"
                                                    onClick={() => togglePasswordVisibility('confirm')}
                                                    aria-label={showPassword.confirm ? 'Hide password' : 'Show password'}
                                                >
                                                    <img src={showPassword.confirm ? window.getPublicUrl('/assets/images/Eye.svg') : window.getPublicUrl('/assets/images/Eye-closed.svg')} alt="" />
                                                </button>
                                            </div>
                                            {formErrors.confirmPassword && <span className="error-message">{formErrors.confirmPassword}</span>}
                                        </div>

                                        <div className="password-form-actions">
                                            <button
                                                type="submit"
                                                className="submit-btn"
                                                disabled={loader}
                                            >
                                                {loader ? 'Changing...' : 'Change Password'}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Settings;
